import React, { useEffect, useState } from 'react';
import { Smartphone, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  onInstallPromptChange,
  triggerInstallPrompt,
  isStandalone,
} from '@/lib/pwa';

const DISMISS_KEY = 'pwa_install_dismissed_at';
const SUPPRESS_HOURS = 7 * 24; // hide for a week after a dismissal

/**
 * Bottom-bar install CTA. Mounts globally and only appears when:
 *   - The browser fired `beforeinstallprompt` (Chrome/Edge/Android).
 *   - The user hasn't dismissed it in the last week.
 *   - The app isn't already running standalone.
 *
 * iOS Safari never fires `beforeinstallprompt`, so we show a separate
 * one-time hint with the "Add to Home Screen" instructions instead.
 */
const InstallPWABanner = () => {
  const [installable, setInstallable] = useState(false);
  const [hidden, setHidden] = useState(true);
  const [iosHint, setIosHint] = useState(false);

  useEffect(() => {
    if (isStandalone()) return;
    const dismissedAt = parseInt(localStorage.getItem(DISMISS_KEY) || '0', 10);
    const suppressUntil = dismissedAt + SUPPRESS_HOURS * 3600 * 1000;
    if (Date.now() < suppressUntil) return;

    const unsub = onInstallPromptChange((e) => {
      setInstallable(!!e);
      if (e) setHidden(false);
    });

    // iOS Safari: detect and show install hint if not yet dismissed.
    const ua = window.navigator.userAgent;
    const isIos = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    const isSafari = /^((?!chrome|android).)*safari/i.test(ua);
    if (isIos && isSafari) {
      setIosHint(true);
      setHidden(false);
    }
    return unsub;
  }, []);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setHidden(true);
  };

  const install = async () => {
    const res = await triggerInstallPrompt();
    if (res.outcome === 'accepted' || res.outcome === 'unavailable') {
      setHidden(true);
    }
  };

  if (hidden || (!installable && !iosHint)) return null;

  return (
    <div
      className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-4 sm:bottom-4 sm:w-[380px] z-[100] rounded-lg border border-primary/40 bg-card/95 backdrop-blur shadow-2xl p-4 flex items-start gap-3"
      data-testid="pwa-install-banner"
    >
      <div className="w-9 h-9 rounded-md bg-primary/10 flex items-center justify-center shrink-0">
        <Smartphone className="w-4 h-4 text-primary" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-bold uppercase tracking-tight">Install NextGen</p>
        {iosHint ? (
          <p className="text-xs text-muted-foreground mt-1 leading-snug">
            Tap the <strong>Share</strong> icon below, then "Add to Home Screen" to install. Works offline.
          </p>
        ) : (
          <p className="text-xs text-muted-foreground mt-1 leading-snug">
            Add the app to your home screen. Works offline and launches like a native app.
          </p>
        )}
        {installable && (
          <Button
            size="sm"
            className="mt-3 font-bold uppercase tracking-widest"
            onClick={install}
            data-testid="pwa-install-btn"
          >
            Install app
          </Button>
        )}
      </div>
      <button
        type="button"
        onClick={dismiss}
        className="text-muted-foreground hover:text-foreground shrink-0"
        aria-label="Dismiss"
        data-testid="pwa-dismiss-btn"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};

export default InstallPWABanner;
